/**
 * One-line group-tag row with a `+N` overflow counter — the Groups cell of the Profiles table.
 *
 * The cell has to be exactly one line tall in every row: a wrapping tag list made row heights depend
 * on group membership, so adding a group to one profile re-flowed the table. How many tags fit is a
 * layout question (tag width follows the group's name), not a character count, so it is **measured**
 * — the same choice `PostText` makes for its "See more" toggle.
 *
 * The measurement reads a **ruler**: a copy of the full list, absolutely positioned and never
 * painted. Because the ruler is out of flow, hiding tags in the visible row cannot change it, so the
 * ResizeObserver can never feed itself a different answer than the one it just produced. Measuring
 * the visible row instead would oscillate — hide a tag, the row narrows, the tag fits again.
 */

import { useLayoutEffect, useRef, useState } from 'react';

export type GroupTagsProps = {
  /** Group names in display order; empty renders the table's "—". */
  names: readonly string[];
};

export function GroupTags({ names }: GroupTagsProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const rulerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(names.length);

  const key = names.join(' ');

  useLayoutEffect(() => {
    const container = containerRef.current;
    const ruler = rulerRef.current;
    if (container === null || ruler === null) return;

    const measure = () => {
      const children = Array.from(ruler.children) as HTMLElement[];
      // The ruler's last child is the `+N` chip; the rest are the tags, in order.
      const counter = children[children.length - 1];
      const tags = children.slice(0, -1);
      const gap = 4;
      const available = container.clientWidth;

      const fits = (limit: number) => {
        let used = 0;
        let count = 0;
        for (const tag of tags) {
          const next = used + (count === 0 ? 0 : gap) + tag.offsetWidth;
          if (next > limit) break;
          used = next;
          count += 1;
        }
        return count;
      };

      const all = fits(available);
      // Everything fits: no counter, so no reserved space either. Otherwise re-run against the
      // narrower budget the counter leaves behind — reserving it up front would hide a tag that
      // would have fitted on its own.
      setVisible(
        all === tags.length ? tags.length : fits(available - counter.offsetWidth - gap),
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [key]);

  const hidden = names.length - visible;

  return (
    <span className="group-tags" ref={containerRef} title={names.join(', ')}>
      <span className="group-tags__ruler" aria-hidden="true" ref={rulerRef}>
        {names.map((name, index) => (
          <span className="tag" key={`${name} ${index}`}>
            {name}
          </span>
        ))}
        <span className="tag">+{names.length}</span>
      </span>
      {names.slice(0, visible).map((name, index) => (
        <span className="tag" key={`${name} ${index}`}>
          {name}
        </span>
      ))}
      {hidden > 0 ? <span className="tag group-tags__more">+{hidden}</span> : null}
    </span>
  );
}

export default GroupTags;
